"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, Loader2, Plug, Search } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConnectors } from "@/hooks/use-connectors";
import { useFetchClient } from "@/lib/fetch-client";
import type { LoungeConnectorId } from "@llmgateway/shared/lounge-connectors";
type ConnectorAction = {
  id: LoungeConnectorId;
} & ({
  action: "connect";
} | {
  action: "disconnect";
} | {
  action: "toggle";
  enabled: boolean;
});
export function ConnectorsDialog() {
  const $ = _c(83);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("connector_status");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [open, setOpen] = useState(Boolean(t0));
  const [search, setSearch] = useState("");
  const [shop, setShop] = useState("");
  const {
    data,
    isPending,
    error,
    refetch
  } = useConnectors();
  const client = useFetchClient();
  const queryClient = useQueryClient();
  let t1;
  if ($[2] !== client || $[3] !== shop) {
    t1 = async action => {
      const params = {
        path: {
          connectorId: action.id
        }
      };
      if (action.action === "connect") {
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.delete("connector");
        returnUrl.searchParams.delete("connector_status");
        const response = await client.POST("/connectors/{connectorId}/authorize", {
          params,
          body: {
            returnTo: returnUrl.pathname + returnUrl.search,
            ...(action.id === "shopify" ? {
              shop
            } : {})
          }
        });
        if (!response.data) {
          throw new Error(response.error?.message ?? "Could not start authorization. Please try again.");
        }
        window.location.assign(response.data.url);
      } else {
        if (action.action === "disconnect") {
          const response_0 = await client.DELETE("/connectors/{connectorId}", {
            params
          });
          if (!response_0.data) {
            throw new Error("Could not disconnect. Please try again.");
          }
        } else {
          const response_1 = await client.PATCH("/connectors/{connectorId}", {
            params,
            body: {
              enabled: action.enabled
            }
          });
          if (!response_1.data) {
            throw new Error("Could not update this connector. Please try again.");
          }
        }
      }
    };
    $[2] = client;
    $[3] = shop;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== queryClient) {
    t2 = async () => {
      await queryClient.invalidateQueries({
        queryKey: ["get", "/connectors"]
      });
    };
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: _temp
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const mutation = useMutation(t3);
  let t4;
  if ($[10] !== data?.connectors) {
    t4 = data?.connectors.filter(_temp2).length ?? 0;
    $[10] = data?.connectors;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const enabledCount = t4;
  let T0;
  let T1;
  let connectors;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[12] !== data?.connectors || $[13] !== enabledCount || $[14] !== error || $[15] !== isPending || $[16] !== mutation || $[17] !== open || $[18] !== refetch || $[19] !== search || $[20] !== searchParams || $[21] !== shop) {
    connectors = data?.connectors.filter(connector_0 => `${connector_0.name} ${connector_0.description}`.toLowerCase().includes(search.toLowerCase()));
    T1 = Dialog;
    t12 = open;
    t13 = setOpen;
    let t15;
    let t16;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Plug className="size-4" />;
      t16 = <span className="hidden sm:inline">Connectors</span>;
      $[35] = t15;
      $[36] = t16;
    } else {
      t15 = $[35];
      t16 = $[36];
    }
    let t17;
    if ($[37] !== enabledCount) {
      t17 = enabledCount > 0 && <span className="bg-primary/10 text-primary rounded-full px-1.5 text-xs tabular-nums">{enabledCount}</span>;
      $[37] = enabledCount;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    if ($[39] !== t17) {
      t14 = <DialogTrigger asChild={true}><Button variant="ghost" size="sm" aria-label="Connectors" className="gap-2">{t15}{t16}{t17}</Button></DialogTrigger>;
      $[39] = t17;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    T0 = DialogContent;
    t9 = "flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl";
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <DialogHeader className="shrink-0 space-y-3 border-b px-6 pt-6 pb-5"><div className="text-primary flex items-center gap-2 text-xs font-medium uppercase tracking-widest"><Plug className="size-3.5" /> Your workspace, at the table</div><DialogTitle className="text-2xl">Connectors</DialogTitle><DialogDescription>Bring context from your apps into The Lounge. Connect an account, then ask about it here. You review tool calls before they run.</DialogDescription></DialogHeader>;
      $[41] = t10;
    } else {
      t10 = $[41];
    }
    let t18;
    if ($[42] !== searchParams) {
      t18 = searchParams.get("connector_status") === "failed" && <p role="alert" className="text-destructive mb-3 text-sm">Could not finish connecting. Please try again.</p>;
      $[42] = searchParams;
      $[43] = t18;
    } else {
      t18 = $[43];
    }
    let t19;
    if ($[44] !== searchParams) {
      t19 = searchParams.get("connector_status") === "connected" && <p role="status" className="mb-3 flex items-center gap-2 text-sm"><Check className="text-primary size-4" /> Your account is connected.</p>;
      $[44] = searchParams;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    let t20;
    if ($[46] !== searchParams) {
      t20 = searchParams.get("connector_status") === "cancelled" && <p role="status" className="text-muted-foreground mb-3 text-sm">Connection cancelled. You can try again whenever you’re ready.</p>;
      $[46] = searchParams;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    let t21;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <Search className="text-muted-foreground absolute top-2.5 left-3 size-4" />;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = event => setSearch(event.target.value);
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== search) {
      t23 = <div className="relative">{t21}<Input aria-label="Search connectors" placeholder={"Find an app\u2026"} value={search} onChange={t22} className="pl-9" /></div>;
      $[50] = search;
      $[51] = t23;
    } else {
      t23 = $[51];
    }
    if ($[52] !== t18 || $[53] !== t19 || $[54] !== t20 || $[55] !== t23) {
      t11 = <div className="shrink-0 px-6 pt-4">{t18}{t19}{t20}{t23}</div>;
      $[52] = t18;
      $[53] = t19;
      $[54] = t20;
      $[55] = t23;
      $[56] = t11;
    } else {
      t11 = $[56];
    }
    t5 = "min-h-0 max-h-[52dvh] overflow-y-auto px-6 py-2";
    if ($[57] !== isPending) {
      t6 = isPending && <p role="status" className="text-muted-foreground py-8 text-center text-sm">Loading connectors…</p>;
      $[57] = isPending;
      $[58] = t6;
    } else {
      t6 = $[58];
    }
    if ($[59] !== error || $[60] !== refetch) {
      t7 = error && <div role="alert" className="py-8 text-center text-sm"><p>Could not load your connectors.</p><Button variant="outline" className="mt-3" onClick={() => void refetch()}>Try again</Button></div>;
      $[59] = error;
      $[60] = refetch;
      $[61] = t7;
    } else {
      t7 = $[61];
    }
    t8 = connectors?.map(connector_1 => {
      const busy = mutation.isPending && mutation.variables.id === connector_1.id;
      return <div key={connector_1.id} className="border-border/70 border-b py-4 last:border-0"><div className="flex items-center gap-3"><div aria-hidden="true" className="bg-muted text-foreground flex size-10 shrink-0 items-center justify-center rounded-xl border text-sm font-semibold">{connector_1.name.slice(0, 2)}</div><div className="min-w-0 flex-1"><h3 className="text-sm font-medium">{connector_1.name}</h3><p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">{connector_1.description}</p></div>{connector_1.connected ? <Switch aria-label={`Use ${connector_1.name} in chats`} checked={connector_1.available && connector_1.enabled} disabled={!connector_1.available || mutation.isPending} onCheckedChange={enabled => mutation.mutate({
            action: "toggle",
            id: connector_1.id,
            enabled
          })} /> : <Button variant="outline" size="sm" disabled={!connector_1.available || mutation.isPending || connector_1.id === "shopify" && !shop} onClick={() => mutation.mutate({
            action: "connect",
            id: connector_1.id
          })}>{busy ? <Loader2 className="size-3.5 animate-spin" /> : connector_1.available ? <ExternalLink className="size-3.5" /> : null}{connector_1.available ? "Connect" : "Not configured"}</Button>}</div>{connector_1.id === "shopify" && !connector_1.connected && connector_1.available && <div className="mt-3 ml-13"><Label htmlFor="connector-shop" className="text-xs">Store domain</Label><Input id="connector-shop" placeholder="your-store.myshopify.com" value={shop} onChange={event_0 => setShop(event_0.target.value.trim().toLowerCase())} className="mt-1" /></div>}{connector_1.connected && <div className="mt-2 ml-13 flex items-center gap-3"><span className="text-muted-foreground text-xs">{!connector_1.available ? "Not configured" : connector_1.enabled ? "Active in chats" : "Paused"}</span><button type="button" disabled={!connector_1.available || mutation.isPending} onClick={() => mutation.mutate({
            action: "connect",
            id: connector_1.id
          })} className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4">Reconnect</button><button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate({
            action: "disconnect",
            id: connector_1.id
          })} className="text-muted-foreground hover:text-destructive text-xs underline underline-offset-4">Disconnect</button></div>}</div>;
    });
    $[12] = data?.connectors;
    $[13] = enabledCount;
    $[14] = error;
    $[15] = isPending;
    $[16] = mutation;
    $[17] = open;
    $[18] = refetch;
    $[19] = search;
    $[20] = searchParams;
    $[21] = shop;
    $[22] = T0;
    $[23] = T1;
    $[24] = connectors;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    T0 = $[22];
    T1 = $[23];
    connectors = $[24];
    t10 = $[25];
    t11 = $[26];
    t12 = $[27];
    t13 = $[28];
    t14 = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  let t15;
  if ($[62] !== connectors?.length) {
    t15 = connectors?.length === 0 && <p className="text-muted-foreground py-8 text-center text-sm">No apps match your search.</p>;
    $[62] = connectors?.length;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== t15 || $[65] !== t5 || $[66] !== t6 || $[67] !== t7 || $[68] !== t8) {
    t16 = <div className={t5}>{t6}{t7}{t8}{t15}</div>;
    $[64] = t15;
    $[65] = t5;
    $[66] = t6;
    $[67] = t7;
    $[68] = t8;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  let t17;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="text-muted-foreground bg-muted/30 shrink-0 border-t px-6 py-4 text-xs leading-relaxed">Connections are private to your account. Results you use in a chat become part of that conversation and are sent to the selected model.</p>;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  let t18;
  if ($[71] !== T0 || $[72] !== t10 || $[73] !== t11 || $[74] !== t16 || $[75] !== t9) {
    t18 = <T0 className={t9}>{t10}{t11}{t16}{t17}</T0>;
    $[71] = T0;
    $[72] = t10;
    $[73] = t11;
    $[74] = t16;
    $[75] = t9;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  let t19;
  if ($[77] !== T1 || $[78] !== t12 || $[79] !== t13 || $[80] !== t14 || $[81] !== t18) {
    t19 = <T1 open={t12} onOpenChange={t13}>{t14}{t18}</T1>;
    $[77] = T1;
    $[78] = t12;
    $[79] = t13;
    $[80] = t14;
    $[81] = t18;
    $[82] = t19;
  } else {
    t19 = $[82];
  }
  return t19;
}
function _temp2(connector) {
  return connector.available && connector.connected && connector.enabled;
}
function _temp(error_0) {
  return toast.error(error_0.message);
}